import type { ConsentState, SdkConfig } from './types';
import { readCookie, writeCookie, inferCookieDomain } from './storage';
import { log } from './log';

const COOKIE = '_emb_consent';
const ONE_YEAR = 365 * 24 * 60 * 60;

export class Consent {
	private state: ConsentState = { analytics: undefined, marketing: undefined };
	private listeners: Array<() => void> = [];
	private cookieDomain: string | undefined;

	constructor(private config: SdkConfig) {
		this.cookieDomain = config.cookieDomain || inferCookieDomain();
	}

	init(): void {
		const stored = readCookie(COOKIE);
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				this.state.analytics = parsed.analytics;
				this.state.marketing = parsed.marketing;
			} catch {
				/* corrupt cookie — fall through to defaults */
			}
		}

		if (!this.config.consent.required) {
			if (this.state.analytics === undefined) this.state.analytics = this.config.consent.defaults.analytics;
			if (this.state.marketing === undefined) this.state.marketing = this.config.consent.defaults.marketing;
		}

		if (this.signalsDeny()) {
			this.state.analytics = false;
			this.state.marketing = false;
			log('consent: DNT/GPC signal honored, tracking disabled');
		}
	}

	private signalsDeny(): boolean {
		if (typeof navigator === 'undefined') return false;
		if (this.config.respectDNT) {
			const dnt = (navigator as any).doNotTrack;
			if (dnt === '1' || dnt === 'yes') return true;
		}
		if (this.config.respectGPC) {
			if ((navigator as any).globalPrivacyControl === true) return true;
		}
		return false;
	}

	canTrack(): boolean {
		return this.state.analytics === true;
	}

	canSetCookies(): boolean {
		return this.state.analytics === true;
	}

	set(next: Partial<ConsentState>): void {
		if (this.signalsDeny()) {
			log('consent: ignoring set() because DNT/GPC denies tracking');
			return;
		}
		if (next.analytics !== undefined) this.state.analytics = next.analytics;
		if (next.marketing !== undefined) this.state.marketing = next.marketing;
		writeCookie(COOKIE, JSON.stringify(this.state), {
			maxAgeSeconds: ONE_YEAR,
			domain: this.cookieDomain,
		});
		log('consent: updated', this.state);
		this.listeners.slice().forEach((cb) => {
			try {
				cb();
			} catch (e) {
				log('consent listener error', e);
			}
		});
	}

	getState(): ConsentState {
		return { ...this.state };
	}

	onChange(cb: () => void): void {
		this.listeners.push(cb);
	}
}
